import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put } from '@/shared/api/api-client';
import type {
  OrgBillingSettings,
  UpdateBillingSettingsRequest,
} from '@/types/settings-api';

/**
 * Org-level billing settings — read.
 *
 * `GET /api/Billing/Settings/{organizationId}`
 *
 * Flutter source: `settings_data_source_impl.dart:1276-1289` (`fetchBillingSettings`).
 * Path segment, NOT a `?organizationId=` query parameter. Original React URL
 * (`/api/Organization/BillingSettings?organizationId=...`) was a REST guess —
 * never existed on the .NET backend; surfaced as a 500/exception in dev.
 * Fixed 2026-05-13 (the same class of drift as PROD-757/759/760 — should have
 * been caught alongside those).
 */
export function useBillingSettings(organizationId: string | null | undefined) {
  return useQuery({
    queryKey: ['settings', 'billing-settings', organizationId],
    queryFn: ({ signal }) => {
      if (!organizationId) throw new Error('useBillingSettings called without orgId');
      return get<OrgBillingSettings>(`/api/Billing/Settings/${organizationId}`, { signal });
    },
    enabled: !!organizationId,
  });
}

/**
 * Org-level billing settings — write.
 *
 * `PUT /api/Billing/Settings/{organizationId}` with the update body.
 *
 * Flutter source: `settings_data_source_impl.dart:1292-1304` (`updateBillingSettings`).
 * The `organizationId` is BOTH a path segment AND a body field (Flutter parity —
 * redundant but the backend expects it). The body shape (`{organizationId,
 * followUpValidity}`) is per the existing `UpdateBillingSettingsRequest` type;
 * if the live PUT response surfaces more required fields, request a cURL.
 */
export function useUpdateBillingSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: UpdateBillingSettingsRequest) =>
      put<unknown>(`/api/Billing/Settings/${req.organizationId}`, req),
    onSuccess: (_data, req) => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'billing-settings', req.organizationId],
      });
    },
  });
}
